/*
 * SQ-415: the plan math, the web's copy (also PlanPricing.cs on the server
 * and PlanPricing.kt on Android; all three run
 * docs/fixtures/plan-pricing-vectors.json). The figures are not here: the
 * server sends its catalog (backend/Accusite/Api/Catalog/plan-catalog.json)
 * with the plan page, and the server prices what is actually charged.
 */

export interface PlanTier {
  estimatesPerMonth: number;
  monthlyPrice: number;
}

export interface PlanCatalog {
  tiers: PlanTier[];
  /** One estimate past the plan, bought on its own. */
  singleRate: number;
  /** Per estimate a month above the top tier. */
  flatRate: number;
  /** A year up front. */
  annualDiscount: number;
  /** Moving up part way through a year. */
  annualUpgradeDiscount: number;
  /** The usage bar turns amber at this share. */
  lowPoolAt: number;
  /** Past the top tier the plan moves in steps of this many. */
  bigStep: number;
}

export type BillingCycle = 'monthly' | 'annual';

/** Money to the cent, half up, so 0.1 + 0.2 never shows as $0.30000000000000004. */
export const cents = (amount: number) => Math.round(amount * 100 + Number.EPSILON * 100) / 100;

export const topTier = (catalog: PlanCatalog) => catalog.tiers[catalog.tiers.length - 1].estimatesPerMonth;

/** Monthly price for `perMonth` estimates a month. */
export const priceFor = (catalog: PlanCatalog, perMonth: number) =>
  catalog.tiers.find(t => t.estimatesPerMonth === perMonth)?.monthlyPrice ?? perMonth * catalog.flatRate;

/** Yearly price: twelve months at the annual discount. */
export const annualFor = (catalog: PlanCatalog, perMonth: number) =>
  cents(priceFor(catalog, perMonth) * 12 * (1 - catalog.annualDiscount));

/** Moving up mid-year: the difference for the months left, at the upgrade discount. */
export const upgradeCost = (catalog: PlanCatalog, from: number, to: number, monthsLeft: number) =>
  cents((priceFor(catalog, to) - priceFor(catalog, from)) * monthsLeft * (1 - catalog.annualUpgradeDiscount));

/** The estimates such an upgrade adds to the year's pool. */
export const upgradeEstimates = (from: number, to: number, monthsLeft: number) => Math.round((to - from) * monthsLeft);

/** What a plan holds over its term: a month's worth, or a year's pool. */
export const allowance = (perMonth: number, annual: boolean) => Math.round(annual ? perMonth * 12 : perMonth);

/** Price of a quantity above the top tier, for a month or for the year. */
export const bigPrice = (catalog: PlanCatalog, total: number, annual: boolean) =>
  cents(annual ? total * catalog.flatRate * (1 - catalog.annualDiscount) : total * catalog.flatRate);

/** The price a plan on record shows: its tier's, or the quantity's above the top tier. */
export const planPrice = (catalog: PlanCatalog, perMonth: number, annual: boolean) => {
  const isTier = catalog.tiers.some(t => t.estimatesPerMonth === perMonth);
  if (isTier) return annual ? annualFor(catalog, perMonth) : priceFor(catalog, perMonth);
  return bigPrice(catalog, allowance(perMonth, annual), annual);
};

const wholeFormat = new Intl.NumberFormat('en-US', {maximumFractionDigits: 0});
const centsFormat = new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});

/** "$1,080" for whole dollars, "$112.50" when there are cents. */
export const planMoney = (amount: number) => {
  const value = cents(amount);
  return '$' + (value % 1 === 0 ? wholeFormat : centsFormat).format(value);
};

/** "$4.50" always to the cent (a rate per estimate). */
export const rateMoney = (amount: number) => '$' + centsFormat.format(amount);

export const count = (n: number) => wholeFormat.format(n);
